// CORE
import React, { useState, useRef, useEffect } from "react";

// THIRD-PARTY LIBRARIES
import { useSelector, useDispatch } from "react-redux";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";

// REDUX
import {
    getNotifications,
    getMoreNotifications,
} from "store/notifications/actions";
import { getNotificationType } from "components/notification/shared/notificationTypes";
import { markAllNotificationsAsRead } from "store/actions";

// ASSETS (SVGs)
import { ReactComponent as NotificationsIcon } from "./assets/svg/notification.svg";
import { ReactComponent as ArrowIcon } from "./assets/svg/arrow-left.svg";

const Notifications = () => {
    const dispatch = useDispatch();
    const { notifications, metadata, loading } = useSelector(
        (state) => state.notifications
    );
    const unreadNotifications = notifications.filter(
        (notification) => !notification.read
    );
    const [pageAll, setPageAll] = useState(1);

    const pageAllRef = useRef(pageAll);
    const loadingRef = useRef(loading);
    const metadataRef = useRef(metadata);

    useEffect(() => {
        pageAllRef.current = pageAll;
    }, [pageAll]);
    useEffect(() => {
        loadingRef.current = loading;
    }, [loading]);
    useEffect(() => {
        metadataRef.current = metadata;
    }, [metadata]);

    useEffect(() => {
        dispatch(getNotifications());
    }, [dispatch]);
    const observerAll = useRef(null);

    useEffect(() => {
        observerAll.current = new IntersectionObserver((entries) => {
            if (
                entries[0].isIntersecting &&
                metadataRef.current?.totalResults
            ) {
                if (
                    loadingRef.current ||
                    pageAllRef.current >=
                        Math.ceil(
                            metadataRef.current.totalResults /
                                metadataRef.current.pagination.limit
                        )
                )
                    return;
                const nextPage = pageAllRef.current + 1;
                dispatch(getMoreNotifications({ page: nextPage }));
                setPageAll(nextPage);
            }
        });
        return () => observerAll.current?.disconnect();
    }, [dispatch]);
    const renderNotifications = () => {
        if (!notifications?.length > 0)
            return (
                <div className="no-notifications">
                    <FormattedMessage id="noNotifications" />
                </div>
            );

        return (
            <ul>
                {notifications?.map((notification, index) => {
                    const notificationTemplate = getNotificationType(
                        notification,
                        index
                    );
                    if (!notificationTemplate?.Template) {
                        console.log("no template", notification.type);
                        return null;
                    }
                    const { Template, data } = notificationTemplate;
                    return (
                        <div
                            key={index}
                            ref={
                                index === notifications.length - 1
                                    ? (node) => {
                                          if (node)
                                              observerAll.current?.observe(
                                                  node
                                              );
                                      }
                                    : null
                            }
                        >
                            <Template
                                notification={notification}
                                key={index}
                                {...data}
                            />
                        </div>
                    );
                })}
            </ul>
        );
    };
    const [notificationsControlOpen, setNotificationsControlOpen] =
        useState(false);
    const notificationsViewRef = useRef(null);
    const toggleNotificationsDropdown = () =>
        setNotificationsControlOpen(!notificationsControlOpen);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                notificationsViewRef.current &&
                !notificationsViewRef.current.contains(event.target)
            ) {
                setNotificationsControlOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);
    const [ripple, setRipple] = useState(false);
    const prevUnreadCount = useRef(unreadNotifications.length);
    const rippleTimeoutRef = useRef(null);

    useEffect(() => {
        if (unreadNotifications.length > prevUnreadCount.current) {
            if (rippleTimeoutRef.current) {
                clearTimeout(rippleTimeoutRef.current);
            }
            setRipple(false);
            setTimeout(() => {
                setRipple(true);
            }, 50);
            rippleTimeoutRef.current = setTimeout(() => {
                setRipple(false);
            }, 3000);
        }
        prevUnreadCount.current = unreadNotifications.length;
    }, [unreadNotifications]);

    return (
        <div className="dropdown-container" ref={notificationsViewRef}>
            <div
                className={`dropdown-toggler ${ripple ? "ripple-effect" : ""}`}
                onClick={toggleNotificationsDropdown}
            >
                <NotificationsIcon />
                {unreadNotifications.length > 0 && (
                    <div className="notifications-count">
                        {unreadNotifications.length}
                    </div>
                )}
            </div>

            <div
                className={`dropdown-menu ${
                    notificationsControlOpen ? "fade-in" : "fade-out"
                }`}
            >
                <div className="dropdown-content">
                    <div className="notification-head">
                        <span>
                            <FormattedMessage id="notifications" />
                        </span>
                        {notifications?.filter((notif) => notif?.read === false)
                            .length > 0 && (
                            <button
                                type="button"
                                className="mark-all"
                                onClick={() => {
                                    dispatch(markAllNotificationsAsRead());
                                }}
                            >
                                <FormattedMessage id="markAllAsRead" />
                            </button>
                        )}
                    </div>
                    {renderNotifications()}
                    <div className="notification-footer">
                        <Link to="/notifications">
                            <FormattedMessage id="allNotifications" />
                            <ArrowIcon />
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Notifications;
